import {
  Button,
  Header,
  Link,
  Table,
} from '@cloudscape-design/components';
import { Formik } from 'formik';
import { useGetTravelersByIds } from 'hooks/use-basicQueries';
import PropTypes from 'prop-types';
import React, {
  useMemo,
} from 'react';

export default function AddTravelersTable({ scannedTravelers = [], onAddTravelers }) {
  const {
    data: findTravelersByIds = [], isPending, isError,
  } = useGetTravelersByIds(scannedTravelers);
  const travelerIdSet = useMemo(
    () => new Set(findTravelersByIds.map((t) => t.id)),
    [findTravelersByIds],
  );
  // Until the lookup has actually resolved, we can't tell new from
  // existing — keep every row unselectable so an in-flight or failed
  // query can't let an existing traveler be re-added (and overwritten).
  const lookupResolved = !isPending && !isError;

  if (!scannedTravelers?.length) return null;

  return (
    <Formik
      initialValues={{ selectedItems: [] }}
      onSubmit={async (values) => {
        if (!values.selectedItems?.length) return;
        onAddTravelers(values.selectedItems);
      }}
    >
      {({
        values, handleSubmit, setFieldValue, isSubmitting,
      }) => (
        <form onSubmit={handleSubmit}>
          <Table
            variant="embedded"
            header={(
              <Header
                variant="h3"
                actions={(
                  <Button loading={isSubmitting} disabled={!values.selectedItems?.length} type="submit" variant="primary">
                    Add selected travelers
                  </Button>
                )}
              >
                Scan Results
              </Header>
            )}
            onSelectionChange={({ detail }) => setFieldValue('selectedItems', detail.selectedItems)}
            selectedItems={values.selectedItems}
            selectionType="multi"
            isItemDisabled={(item) => !lookupResolved || travelerIdSet.has(item)}
            loading={isPending}
            loadingText="Checking for existing travelers"
            columnDefinitions={[
              {
                header: 'Traveler ID',
                // eslint-disable-next-line react/no-unstable-nested-components
                cell: (item) => (
                  travelerIdSet.has(item) ? (
                    <span>
                      <Link href={`/travelers/${item}`} external>
                        {`${item}`}
                      </Link>
                      {' '}
                      <span>(Existing Traveler)</span>
                    </span>
                  ) : (
                    <span>
                      {`${item} (New Traveler!)`}
                    </span>
                  )
                ),
              },
            ]}
            items={scannedTravelers}
          />
        </form>
      )}
    </Formik>
  );
}

AddTravelersTable.propTypes = {
  scannedTravelers: PropTypes.arrayOf(PropTypes.string),
  onAddTravelers: PropTypes.func.isRequired,
};
